import {
  BorrowerFileType,
  DocumentationRequiredType,
  DocumentStatus,
  FileType,
  getAllDocumentationRequiredByLoanId,
  getFilesFromLoanIdAndFileType,
  getRequiredFileTypesByDocumentationRequiredType,
  getRequiredFileTypesByDocumentationRequiredTypeAndPaymentMethodType,
  LoanFileType,
  patchRemoveRequiredFileTypeFromDocumentationRequired,
  patchSetAnnotationToDocumentationRequired,
  patchSetDocumentRequiredIsNotSent,
  patchSetDocumentStatus,
  PaymentMethodType,
  RequiredFile,
} from '@nemuru-eng/js-api-client';
import { useState } from 'react';

import { useDocumentationRequiredFilesActions } from '@/components/modules/Documentation/components/DocumentationDetails/hooks/useDocumentationRequiredFilesActions';
import { useDownloadDocument } from '@/components/modules/Documentation/components/DocumentationDetails/hooks/useDownloadDocument';
import {
  SetDocumentStatusSchema,
  SetDocumentStatusType,
} from '@/components/modules/Documentation/components/DocumentationDetails/schemas/SetDocumentStatusSchema';
import { DocumentDetailsAction } from '@/components/modules/Documentation/components/DocumentationDetails/types/documentDetailsActions';
import { DocumentDetailsStandardAction } from '@/components/modules/Documentation/components/DocumentationHeader/types/DocumentDetailsStandardAction';
import { DocumentationMode } from '@/components/modules/Documentation/types/DocumentationMode';
import { DocumentManagerActionDialogParams } from '@/components/modules/Documentation/types/DocumentManagerActionDialogParams';
import { DocumentManagerStandardActionDialogParams } from '@/components/modules/Documentation/types/DocumentManagerStandardActionDialogParams';
import { Font } from '@/components/shared/components/Font';
import { ReadOnlyDataRow } from '@/components/shared/components/ReadOnlyDataRow';
import { Text } from '@/components/shared/components/Text';
import { useDialog } from '@/components/shared/hooks/useDialog';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { useCache } from '@/hooks/requests/useCache';
import { useRequest } from '@/hooks/requests/useRequest';
import { isEnumValue } from '@/utils/isEnumValue';

interface UseRequiredFileProps {
  requiredFile: RequiredFile;
  documentationRequiredType: DocumentationRequiredType;
  documentationRequiredId: string;
  paymentMethodType: PaymentMethodType;
  paymentMethodId: string;
  mode: DocumentationMode;
  borrowerId?: string;
}

export const useRequiredFile = ({
  requiredFile,
  documentationRequiredType,
  documentationRequiredId,
  paymentMethodType,
  paymentMethodId,
  mode,
  borrowerId,
}: UseRequiredFileProps) => {
  const { request, loading } = useRequest();
  const { getTextIdTranslation } = useTranslation();
  const { revalidate } = useCache(
    getAllDocumentationRequiredByLoanId,
    { loanId: paymentMethodId },
    { revalidateOnMount: false },
  );

  const documentationRequiredFileTypes = useCache(
    getRequiredFileTypesByDocumentationRequiredType,
    {
      documentationRequiredType,
    },
    {
      revalidateIfStale: false,
    },
  ).data;

  const { opened, setOpened, toggleDialog } = useDialog();

  const [actionDialogParams, setActionDialogParams] = useState<DocumentManagerActionDialogParams | undefined>();
  const [actionStandardDialogParams, setActionStandardDialogParams] = useState<
    DocumentManagerStandardActionDialogParams | undefined
  >();
  const [uploadFile, setUploadFile] = useState(false);

  const { getAndCallDownloadFunctionByMimeType } = useDownloadDocument();

  const resetUseRequiredFileState = () => {
    setActionDialogParams(undefined);
    setActionStandardDialogParams(undefined);
    setUploadFile(false);
  };

  const onRemoveRequiredFileTypeFromDocumentationRequired = async () => {
    if (!documentationRequiredType || !documentationRequiredFileTypes) return;
    const fileTypeId: string | undefined = documentationRequiredFileTypes.find(
      (requiredFileType) => requiredFileType.name === requiredFile.name,
    )?.id;
    if (fileTypeId && paymentMethodId) {
      await request(patchRemoveRequiredFileTypeFromDocumentationRequired, {
        id: documentationRequiredId,
        fileTypeId,
        documentationRequiredType,
      });
      setOpened(false);
      setTimeout(() => {
        revalidate();
      }, 1000);
    }
  };

  const onSetDocumentStatus = async (values?: SetDocumentStatusType) => {
    if (!values) return;

    try {
      request(patchSetDocumentStatus, {
        id: documentationRequiredId,
        fileTypeId: requiredFile.id,
        documentationRequiredType,
        documentStatus: values.documentStatus,
      });
      if (values.documentStatus === DocumentStatus.INCIDENCE && values.annotation) {
        request(patchSetAnnotationToDocumentationRequired, {
          id: documentationRequiredId,
          fileTypeId: requiredFile.id,
          documentationRequiredType,
          documentStatus: values.documentStatus,
          annotation: values.annotation,
        });
      }
    } catch (error) {
    } finally {
      setOpened(false);
      setTimeout(() => {
        revalidate();
      }, 1000);
    }
  };

  const onSetDocumentRequiredIsNotSent = async () => {
    await request(patchSetDocumentRequiredIsNotSent, {
      id: documentationRequiredId,
      fileTypeId: requiredFile.id,
      documentationRequiredType,
    });
    setOpened(false);
    setTimeout(() => {
      revalidate();
    }, 1000);
  };

  const getStandardActionDialogParams = (actionType: DocumentDetailsStandardAction) => {
    switch (actionType) {
      case DocumentDetailsStandardAction.REMOVE_REQUIRED_FILE_TYPE_FROM_DOCUMENTATION_REQUIRED:
        return {
          actionType: DocumentDetailsStandardAction.REMOVE_REQUIRED_FILE_TYPE_FROM_DOCUMENTATION_REQUIRED,
          titleTextElement: <Text tId={'REMOVE_REQUIRED_FILE_TYPE_FROM_DOCUMENTATION_REQUIRED'} />,
          actionDescriptionTextElement: (
            <>
              <Text tId={'REMOVE_REQUIRED_FILE_TYPE_FROM_DOCUMENTATION_REQUIRED_DESCRIPTION'} />
              <ReadOnlyDataRow
                labelTextId={'REQUIRED_DOCUMENT_TYPE'}
                value={requiredFile.description}
                boxProps={{ paddingTop: 3 }}
                underlined
              />
            </>
          ),
          actionButtonsArray: [
            {
              onClick: () => onRemoveRequiredFileTypeFromDocumentationRequired(),
              buttonLabelTextId: 'REMOVE',
            },
          ],
        };
      case DocumentDetailsStandardAction.SET_DOCUMENT_REQUIRED_IS_NOT_SENT:
        return {
          actionType: DocumentDetailsStandardAction.SET_DOCUMENT_REQUIRED_IS_NOT_SENT,
          titleTextElement: <Text tId={'SET_DOCUMENT_REQUIRED_IS_NOT_SENT'} />,
          actionDescriptionTextElement: <Text tId={'SET_DOCUMENT_REQUIRED_IS_NOT_SENT_DESCRIPTION'} />,
          actionButtonsArray: [
            {
              onClick: () => onSetDocumentRequiredIsNotSent(),
              buttonLabelTextId: 'SET_NOT_SENT',
            },
          ],
        };
    }
  };

  const getActionDialogParamsByType = (
    actionType: DocumentDetailsAction,
  ): DocumentManagerActionDialogParams | undefined => {
    switch (actionType) {
      case DocumentDetailsAction.SET_DOCUMENT_STATUS:
        return {
          actionType: DocumentDetailsAction.SET_DOCUMENT_STATUS,
          titleTextElement: 'SET_DOCUMENT_STATUS',
          actionDescriptionTextElement: (
            <ActionDescriptionWrapper>
              <Text tId={'SET_DOCUMENT_STATUS_REQUIRED'} />
            </ActionDescriptionWrapper>
          ),
          onSubmitForm: (values) => onSetDocumentStatus(values),
          saveButtonTextId: 'CHANGE_STATUS',
          schema: () => SetDocumentStatusSchema(getTextIdTranslation),
        };
      default:
        break;
    }
  };

  const selectAndSetActionDialogParams = (actionType: DocumentDetailsStandardAction | DocumentDetailsAction) => {
    if (isEnumValue(DocumentDetailsAction, actionType)) {
      const actionDialogParams = getActionDialogParamsByType(actionType);
      setActionStandardDialogParams(undefined);
      setActionDialogParams(actionDialogParams);
    }
    if (isEnumValue(DocumentDetailsStandardAction, actionType)) {
      const standardActionDialogParams = getStandardActionDialogParams(actionType);
      setActionDialogParams(undefined);
      setActionStandardDialogParams(standardActionDialogParams);
    }
  };

  const openActionDialogByActionType = (actionType: DocumentDetailsStandardAction | DocumentDetailsAction) => {
    if (actionType === DocumentDetailsStandardAction.UPLOAD_FILE) {
      setUploadFile(true);
      toggleDialog();
      return;
    }
    selectAndSetActionDialogParams(actionType);
    toggleDialog();
  };

  const onDownloadFile = async () => {
    if (!documentationRequiredType || !documentationRequiredFileTypes || !paymentMethodId) return;
    const fileTypeName: FileType | undefined = documentationRequiredFileTypes.find(
      (requiredFileType) => requiredFileType.name === requiredFile.name,
    )?.name;

    if (!fileTypeName) return;
    const downloadFiles = async () => {
      if (!fileTypeName) return undefined;
      switch (documentationRequiredType) {
        case DocumentationRequiredType.LOAN:
          return getFilesFromLoanIdAndFileType({
            loanId: paymentMethodId,
            documentFileType: fileTypeName as LoanFileType,
          });
        case DocumentationRequiredType.BORROWER:
          if (borrowerId && paymentMethodId) {
            return getRequiredFileTypesByDocumentationRequiredTypeAndPaymentMethodType({
              borrowerId: borrowerId,
              paymentMethodId: paymentMethodId,
              paymentMethodType,
              fileType: fileTypeName as BorrowerFileType,
            });
          }
          break;
        default:
          return undefined;
      }
    };

    const downloadedFiles = await downloadFiles();
    if (!downloadedFiles) return;
    downloadedFiles.forEach((downloadedFile) =>
      getAndCallDownloadFunctionByMimeType(downloadedFile.mimeType, downloadedFile.data, downloadedFile.name),
    );
  };

  const { getActionOptionsArray } = useDocumentationRequiredFilesActions({
    onClickAction: openActionDialogByActionType,
    requiredFile,
    onDownloadFile,
  });

  const actionOptionsArray = getActionOptionsArray(mode);

  return {
    actionStandardDialogParams,
    actionDialogParams,
    setActionDialogParams,
    resetUseRequiredFileState,
    actionOptionsArray,
    uploadFile,
    opened,
    setOpened,
    loading,
    openActionDialogByActionType,
  };
};

export const ActionDescriptionWrapper = ({ children }) => (
  <Font variant={'subtitle2'} color={'textSecondary'}>
    {children}
  </Font>
);
